/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { forwardRef, useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import Tippy from "@tippyjs/react";
import { Icon } from "../../Icons";
import * as styles from "./Input.module.scss";

const TextInputMsg = ({ msg }) => (
	<span role="alert" className={cx("input__msg")}>
		{msg}
	</span>
);

const TextInput = forwardRef(
	(
		{
			size,
			full,
			placeholder,
			helper,
			errorMsg,
			value,
			className,
			onFocus,
			onBlur,
			onChange,
			onKeyPress,
			readOnly,
			label,
			labelHidden,
			disabled,
			error,
			required,
			type,
			id,
			minValue,
			maxValue,
			minLength,
			maxLength,
			tabIndex,
			tooltipShow,
			tooltipIconName,
			tooltipPlacement,
			tooltipContent,
		},
		ref
	) => {
		const tippyRef = useRef();
		const [isSingleLine, setIsSingleLine] = useState(false);
		const [isVisible, setIsVisible] = useState(false);
		const [isUpdated, setIsUpdated] = useState();

		const classes = cx(styles.input, {
			[styles[size]]: size && styles[size],
			[styles.full]: full,
			[styles.error]: error,
			// [styles.required]: required,
			[className]: className,
		});

		useEffect(() => {
			if (tippyRef?.current) {
				const popperRef = tippyRef.current._tippy.popper;
				const contentHeight = popperRef.getElementsByClassName("tippy-content")[0].scrollHeight;
				const lineHeight = parseInt(window.getComputedStyle(document.body).lineHeight, 10);

				setIsSingleLine(contentHeight <= lineHeight);
			}
		}, [isVisible, isUpdated]);

		const handleScroll = () => {
			setIsUpdated(true);
		};

		useEffect(() => {
			window.addEventListener("scroll", handleScroll);

			return () => {
				window.removeEventListener("scroll", handleScroll);
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		return (
			<div className={classes}>
				<label
					className={cx(styles.input__label, {
						[styles.input__labelRequired]: required,
						[styles.input__labelHidden]: labelHidden,
					})}
					htmlFor={`input-${id}`}>
					{label}
				</label>

				<div className={cx({ [styles.input__tooltip]: tooltipShow })}>
					<input
						type={type}
						onFocus={onFocus}
						onChange={onChange}
						onBlur={onBlur}
						onKeyPress={onKeyPress}
						id={`input-${id}`}
						name={`input-${id}`}
						placeholder={placeholder}
						className={cx(styles.input__control, {
							[styles.input__tooltipInput]: tooltipShow,
						})}
						readOnly={readOnly}
						disabled={disabled}
						value={value}
						min={minValue}
						max={maxValue}
						minLength={minLength}
						maxLength={maxLength}
						tabIndex={tabIndex}
						ref={ref}
					/>

					{tooltipShow && (
						<Tippy
							maxWidth={485}
							placement={tooltipPlacement}
							content={tooltipContent}
							disabled={tooltipContent === undefined}
							className={cx([
								styles.input__tooltipContent,
								styles[tooltipPlacement],
								{
									[styles.single]: isSingleLine,
								},
							])}
							arrow={true}>
							<button
								ref={tippyRef}
								onFocus={() => {
									setIsVisible(true);

									setTimeout(() => {
										window.dispatchEvent(new Event("scroll", { bubbles: true }));
									}, 250);
								}}
								onMouseEnter={() => {
									setIsVisible(true);

									setTimeout(() => {
										window.dispatchEvent(new Event("scroll", { bubbles: true }));
									}, 250);
								}}
								onBlur={() => {
									setIsVisible(false);
									setIsUpdated(true);
								}}
								type="button"
								className={styles.input__tooltipButton}>
								<Icon name={tooltipIconName} />
							</button>
						</Tippy>
					)}
				</div>
				{helper && !error && <TextInputMsg msg={helper} />}
				{error && <TextInputMsg msg={errorMsg} />}
			</div>
		);
	}
);

TextInput.propTypes = {
	size: PropTypes.oneOf(["sm", "md", "lg"]),
	type: PropTypes.oneOf(["text", "email", "tel", "password"]),
	label: PropTypes.string.isRequired,
	id: PropTypes.string.isRequired,
	labelHidden: PropTypes.bool,
	placeholder: PropTypes.string,
	helper: PropTypes.string,
	errorMsg: PropTypes.string,
	full: PropTypes.bool,
	required: PropTypes.bool,
	readOnly: PropTypes.bool,
	disabled: PropTypes.bool,
	error: PropTypes.bool,
	value: PropTypes.string,
	minValue: PropTypes.string,
	maxValue: PropTypes.string,
	minLength: PropTypes.string,
	maxLength: PropTypes.string,
	onFocus: PropTypes.func,
	onBlur: PropTypes.func,
	onChange: PropTypes.func,
	onKeyPress: PropTypes.func,
	className: PropTypes.string,
	tabIndex: PropTypes.string,
	tooltipShow: PropTypes.bool,
	tooltipPlacement: PropTypes.string,
	tooltipContent: PropTypes.node,
	tooltipIconName: PropTypes.string,
};

TextInput.defaultProps = {
	size: "md",
	type: "text",
	placeholder: "",
	helper: "",
	required: false,
	labelHidden: false,
	errorMsg: "",
	onFocus: () => {},
	onChange: () => {},
	onBlur: () => {},
	onKeyPress: () => {},
	full: true,
	readOnly: false,
	error: false,
	disabled: false,
	value: "",
	minValue: undefined,
	maxValue: undefined,
	minLength: undefined,
	maxLength: undefined,
	className: "",
	tabIndex: undefined,
	tooltipShow: false,
	tooltipPlacement: "bottom",
	tooltipContent: undefined,
	tooltipIconName: "info--o",
};

TextInputMsg.propTypes = {
	msg: PropTypes.string,
};

TextInputMsg.defaultProps = {
	msg: "",
};

export default TextInput;
export { TextInput };
